/**
 * The browser half of `chi auth login`. One question, two answers.
 *
 * IT ASKS NOTHING ABOUT PLANS. "Do I trust this terminal?" is a security question that
 * has to be read, and anything commercial next to it competes for the attention the
 * security question needs — so the part that matters is the part that gets skimmed.
 *
 * There is also nothing to ask. The account is on Archis Free the moment it exists, and
 * moving to Team is a workspace and its seats, which you set up by inviting people in
 * the portal. Nothing is lost either way: the CLI never reads the plan's name. It gates
 * commands on the feature keys carried in the signed lease.
 *
 * REJECT IS A REAL BUTTON. It used to be a "Not now" link, which left the terminal spinning
 * for the full ten minutes — teaching the one person who most needed to act that the
 * control did nothing.
 */

import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import { useSession } from '@/app/providers'
import { ApiError } from '@/shared/api/client'
import { useApproveActivation, useDenyActivation, usePendingActivationById } from '@/shared/api/hooks'
import { Masthead } from '@/shared/ui/Chrome'
import {
  Button,
  ErrorNotice,
  Loading,
  formatRelative,
  platformLabel,
} from '@/shared/ui/primitives'

type Outcome = 'connected' | 'rejected'

/**
 * Whether a redirect target is a loopback address — and therefore whether it can
 * only be reached by a CLI on the same machine as this browser.
 *
 * That is the fact the fallback below hangs on. `http://127.0.0.1:54321/callback`
 * works perfectly when somebody ran `chi auth login` on the laptop they are
 * reading this page on, and cannot possibly work when they ran it over SSH on a
 * cloud box: the navigation goes to *this* machine's loopback, where nothing is
 * listening, and the browser shows ERR_CONNECTION_REFUSED.
 *
 * Parsed rather than string-matched on `127.0.0.1`, because `localhost`, `[::1]`
 * and `127.0.0.2` are all the same situation and a substring test would miss
 * three of the four.
 */
function loopbackCode(redirectTo: string | undefined): string | null {
  if (!redirectTo) return null
  try {
    const url = new URL(redirectTo)
    const host = url.hostname.replace(/^\[|\]$/g, '')
    const isLoopback = host === 'localhost' || host === '::1' || /^127\./.test(host)
    if (!isLoopback) return null
    return url.searchParams.get('code')
  } catch {
    return null
  }
}

export function Authorize() {
  const [params] = useSearchParams()
  const { user, ready } = useSession()
  const sessionId = params.get('session') ?? ''

  const { data: pending, isPending, error } = usePendingActivationById(user ? sessionId : '')
  const approve = useApproveActivation()
  const deny = useDenyActivation()
  const [outcome, setOutcome] = useState<Outcome | null>(null)

  const [fallback, setFallback] = useState<string | null>(null)

  const busy = approve.isPending || deny.isPending
  const failure = approve.error ?? deny.error

  // Both answers end the same way: hand the browser back to the terminal. The redirect
  // carries the one-time code on approval and an error on rejection, so the CLI either
  // finishes or fails at once instead of waiting out its timeout.
  //
  // WHY THE CODE IS SHOWN BEFORE THE HAND-OFF, on a loopback redirect. That redirect can
  // only be delivered by a CLI on the same machine as this browser. Over SSH the CLI is on
  // a different one: the navigation lands on *this* machine's loopback, nothing is
  // listening, and the person gets ERR_CONNECTION_REFUSED on a page that has already
  // replaced this one — so the code they now need is gone from the screen along with any
  // explanation of what happened.
  //
  // Rendering it first costs a beat on the flow that works and saves the flow that does
  // not. It is also the only order that *can* work: after the navigation this page is
  // unloaded, and there is nothing left to render a fallback into.
  function handOff(redirectTo: string | undefined, result: Outcome) {
    const code = result === 'connected' ? loopbackCode(redirectTo) : null
    if (redirectTo && code) {
      setFallback(code)
      // assign(), not replace(), on this path only: when the navigation fails there is
      // nothing to go forward to, and Back has to come back *here* — to the code. On the
      // path that succeeds the code is already spent, so a return trip renders a page of
      // harmless text. Elsewhere replace() stays, for the reason it always had.
      window.setTimeout(() => window.location.assign(redirectTo), 1200)
      return
    }
    setOutcome(result)
    if (redirectTo) {
      // replace(), not assign(): the URL carries a one-time code, and leaving it in history
      // invites a back-button retry that fails against a code already spent.
      window.location.replace(redirectTo)
    }
  }

  async function onApprove() {
    const r = await approve.mutateAsync(sessionId)
    handOff(r.redirect_to, 'connected')
  }

  async function onReject() {
    const r = await deny.mutateAsync(sessionId)
    handOff(r.redirect_to, 'rejected')
  }

  if (!ready || (user && isPending && sessionId)) {
    return (
      <Frame>
        <Loading label="Checking the request" />
      </Frame>
    )
  }

  if (!sessionId) {
    return (
      <Frame title="Nothing to connect">
        <p className="dim">
          This link is missing its session. Run <code>chi auth login</code> and open the link it
          prints.
        </p>
      </Frame>
    )
  }

  if (!user) {
    return (
      <Frame title="Sign in to continue">
        <p className="dim">
          Your terminal is waiting. Sign in and you will come straight back here.
        </p>
        <div style={{ marginTop: 'var(--s5)' }}>
          <Link
            className="cta cta--primary"
            to={`/signin?next=${encodeURIComponent(`/cli/authorize?session=${sessionId}`)}`}
          >
            Sign in →
          </Link>
        </div>
      </Frame>
    )
  }

  if (error) return <LookupFailure error={error} />

  if (fallback) return <HandingOff code={fallback} />

  if (outcome === 'connected') {
    return (
      <Frame title="Terminal connected">
        <p className="dim">You can close this tab and go back to your terminal.</p>
      </Frame>
    )
  }

  if (outcome === 'rejected') {
    return (
      <Frame title="Request rejected">
        <p className="dim">
          Nothing was connected. If you did not start this, no action is needed — the
          request could not have granted access without this page.
        </p>
      </Frame>
    )
  }

  if (!pending) return null

  return (
    <Frame>
      <p className="mono-note" style={{ margin: 0 }}>
        {user.email}
      </p>
      <h2 style={{ marginTop: 'var(--s3)' }}>Connect this terminal?</h2>
      <p className="dim" style={{ marginTop: 'var(--s3)', maxWidth: '46ch' }}>
        It will be able to use Archis on your account until you remove it.
      </p>

      <div className="panel" style={{ marginTop: 'var(--s5)' }}>
        <dl className="kv">
          <dt>Machine</dt>
          <dd>{pending.display_name || 'Unnamed device'}</dd>
          <dt>System</dt>
          <dd>{platformLabel(pending.platform, pending.arch)}</dd>
          <dt>Archis version</dt>
          <dd>{pending.cli_version}</dd>
          <dt>Requested</dt>
          <dd>{formatRelative(pending.expires_at)} left</dd>
        </dl>
      </div>

      {failure && (
        <div style={{ marginTop: 'var(--s5)' }}>
          <ErrorNotice error={failure} />
        </div>
      )}

      <div className="row" style={{ marginTop: 'var(--s6)', gap: 'var(--s4)' }}>
        <Button onClick={() => void onApprove()} disabled={busy}>
          {approve.isPending ? 'Connecting…' : 'Connect'}
        </Button>
        <Button variant="ghost" onClick={() => void onReject()} disabled={busy}>
          {deny.isPending ? 'Rejecting…' : 'Reject'}
        </Button>
      </div>

      <p className="mono-note" style={{ marginTop: 'var(--s5)', maxWidth: '52ch' }}>
        Reject this if you did not just run <code>chi auth login</code> on the machine above.
      </p>
    </Frame>
  )
}

/**
 * The one screen that exists for the terminal that is somewhere else.
 *
 * On a laptop nobody reads this: the redirect fires a beat later and the CLI finishes.
 * It is written for the other case — somebody who ran `chi auth login` over SSH, whose
 * browser is about to fail to reach a `127.0.0.1` that is not the CLI's — and for them
 * this is the only place the one-time code is ever visible.
 *
 * `chi auth login` now detects an SSH session and uses the code flow instead, which
 * means most people never get here at all. This is the backstop for the ones the
 * detection cannot see: X11 forwarding, a `BROWSER` shim, a container with a tunnel.
 */
function HandingOff({ code }: { code: string }) {
  const [copied, setCopied] = useState(false)

  async function copy() {
    try {
      await navigator.clipboard.writeText(code)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    } catch {
      // A clipboard the browser will not give us is not a failure worth reporting:
      // the code is on screen and selectable, which is the actual fallback.
    }
  }

  return (
    <Frame title="Connecting your terminal…">
      <p className="dim" style={{ maxWidth: '46ch' }}>
        Approved. Your terminal should finish by itself in a moment.
      </p>
      <div className="panel" style={{ marginTop: 'var(--s5)' }}>
        <p className="mono-note" style={{ margin: 0 }}>
          If it does not — because you ran <code>chi auth login</code> on{' '}
          <strong>another machine</strong>, over SSH — paste this code into that terminal:
        </p>
        <div
          className="row ph-no-capture"
          style={{ marginTop: 'var(--s4)', gap: 'var(--s3)', alignItems: 'center' }}
        >
          <code className="ph-no-capture" style={{ userSelect: 'all', wordBreak: 'break-all' }}>
            {code}
          </code>
          <Button variant="ghost" className="ph-no-capture" onClick={() => void copy()}>
            {copied ? 'Copied' : 'Copy'}
          </Button>
        </div>
      </div>
      <p className="mono-note" style={{ marginTop: 'var(--s5)', maxWidth: '52ch' }}>
        Next time, <code>chi auth login</code> on a remote shell skips the redirect and
        shows a verification code in the terminal instead.
      </p>
    </Frame>
  )
}

/**
 * Why the lookup failed, said accurately.
 *
 * Every failure used to render "This request has expired. Run `chi auth login` again",
 * and that is only true for two of them. The endpoint sits behind the portal credential,
 * and that middleware is also where an Archis account is provisioned — so a deployment with
 * signups closed refuses the FIRST login of anybody without an invitation, with a 403 and
 * a message saying exactly that. Reporting it as an expiry hands the person the one
 * instruction guaranteed not to help, and buries the one that would.
 *
 * So: expiry only where expiry is what happened, and the backend's own words everywhere
 * else. It writes those for the person reading them, which is why they are worth showing
 * instead of replacing.
 */
function LookupFailure({ error }: { error: unknown }) {
  const status = error instanceof ApiError ? error.status : 0

  // 400 is what the API returns for an expired session and 404 for one it cannot find.
  // Both mean the same thing to the person: this link is no longer good for anything.
  if (status === 400 || status === 404) {
    return (
      <Frame title="This request has expired">
        <p className="dim">
          Requests are good for ten minutes and can only be used once. Run{' '}
          <code>chi auth login</code> again.
        </p>
      </Frame>
    )
  }

  if (status === 409) {
    return (
      <Frame title="This link was already used">
        <p className="dim">
          A terminal has already been connected with it. If that was not you, remove the
          device from <Link to="/devices">your devices</Link>. Otherwise run{' '}
          <code>chi auth login</code> again for a new link.
        </p>
      </Frame>
    )
  }

  return (
    <Frame title="We could not check this request">
      <ErrorNotice error={error} />
      <p className="dim" style={{ marginTop: 'var(--s5)' }}>
        Your terminal is still waiting. Nothing has been connected.
      </p>
    </Frame>
  )
}

function Frame({ title, children }: { title?: string; children: React.ReactNode }) {
  return (
    <>
      <Masthead />
      <main id="main" className="shell" style={{ paddingBlock: 'var(--s8)', maxWidth: '34rem' }}>
        {title && <h2>{title}</h2>}
        <div style={{ marginTop: title ? 'var(--s4)' : 0 }}>{children}</div>
      </main>
    </>
  )
}
